// src/components/common/PlantOfflineBanner.jsx
//
// Shown over a data panel whose plant agent has gone quiet.
//
// Amber, not red. An offline agent is an operational fact, not a fault in the
// dashboard: the panel keeps showing the last data it received, and this states
// plainly what that data is and how old it is. A red error wall taught everyone
// that the dashboard was broken, which is exactly the impression to avoid.

import { AlertTriangle } from 'lucide-react';
import { relativeTime, absoluteTime } from '../../utils/relativeTime.js';

const AMBER = '#f59e0b';

export default function PlantOfflineBanner({
  plantId,
  since = null,
  lastDataTs = null,
  compact = false,
  now = Date.now(),
}) {
  const who = plantId || 'This plant';

  // lastDataTs needs diagnostics from the agent, which may not be reporting yet.
  // Without it we can still say the agent is offline -- we just cannot date the
  // data on screen, so we say so rather than implying it is current.
  const tail = lastDataTs != null
    ? `showing the last data received ${relativeTime(lastDataTs, now)}`
    : 'no recent data to show';

  return (
    <div
      role="status"
      data-testid="plant-offline-banner"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 6 : 8,
        padding: compact ? '4px 8px' : '8px 12px',
        background: 'rgba(245, 158, 11, 0.12)',
        border: `1px solid ${AMBER}55`,
        borderRadius: 8,
        color: AMBER,
        fontSize: compact ? 11 : 13,
        fontWeight: 600,
        lineHeight: 1.4,
        pointerEvents: 'auto',
      }}
    >
      <AlertTriangle size={compact ? 12 : 15} style={{ flexShrink: 0 }} aria-hidden="true" />
      <span>
        <strong>{who}</strong> is offline
        {since != null && (
          <> since <span title={absoluteTime(since)}>{relativeTime(since, now)}</span></>
        )}
        {' — '}
        {tail}
      </span>
    </div>
  );
}
